import { useEffect } from "react";
import { useMachine } from "@xstate/react";
import {
  ActionTypes,
  assign,
  Machine,
  MachineOptions,
  ErrorPlatformEvent,
  EventObject,
} from "xstate";
import gql from "graphql-tag";
import { MutationTuple } from "@apollo/react-hooks";
import { GraphQLError } from "graphql";
import * as Sentry from "@sentry/browser";

import { useCurrentAccountId } from "@zapier/identity";

import { PaymentProcessor } from "@zapier/payment/esm/types";
import {
  createSetupIntentMutation as createSetupIntentMutationType,
  createSetupIntentMutation_createSetupIntent as SetupIntent,
  createSetupIntentMutationVariables,
} from "@zapier/payment/esm/graphql-types";
import { timeoutAfter } from "@zapier/payment/esm/utils/timeoutAfter";
import { useApolloInjection } from "@zapier/payment/esm/contexts/ApolloInjection";
import {
  ValidationError,
  SetupIntentCreationError,
  FatalError,
  SetupIntentConfirmationError,
} from "@zapier/payment/esm/errors";

type Context = {
  setupIntent?: SetupIntent;
  error?: Error;
};

type Schema = {
  states: {
    boot: Record<string, unknown>;
    idle: Record<string, unknown>;
    setup: Record<string, unknown>;
    setupError: Record<string, unknown>;
    waitForInput: Record<string, unknown>;
    savePaymentSource: Record<string, unknown>;
    errorSaving: Record<string, unknown>;
    finished: Record<string, unknown>;
  };
};

type Event =
  | { type: "ready" }
  | { type: "startSetup" }
  | { type: "retry" }
  | { type: "submit" }
  | { type: "cancel" }
  | (ErrorPlatformEvent & { type: "error.platform.createSetupIntent" })
  | {
      type: `error.platform.savePaymentSource`;
      data:
        | typeof FatalError
        | typeof ValidationError
        | typeof SetupIntentConfirmationError;
    };

const isErrorPlatformEvent = (
  event: EventObject
): event is ErrorPlatformEvent =>
  event.type.startsWith(ActionTypes.ErrorPlatform);

const setupIntentMachine = Machine<Context, Schema, Event>(
  {
    initial: "boot",
    context: {
      setupIntent: undefined,
      error: undefined,
    },
    states: {
      boot: {
        on: {
          ready: "idle",
        },
      },
      idle: {
        on: {
          startSetup: "setup",
        },
      },
      setup: {
        entry: ["clearErrors"],
        // @see https://github.com/statelyai/xstate/issues/1010
        // @ts-ignore - This is a known problem on this version of XState. An update to XState will fix it.
        invoke: {
          src: "createSetupIntent",
          onDone: {
            target: "waitForInput",
            actions: [
              assign({
                setupIntent: (_context, event) => event.data,
              }),
            ],
          },
          onError: {
            target: "setupError",
            actions: [
              assign({
                error: (_context, event) => event.data,
              }),
            ],
          },
        },
      },
      setupError: {
        entry: ["logToSentry", "destroyHostedFields"],
        on: {
          retry: "setup",
          cancel: "finished",
        },
      },
      waitForInput: {
        entry: ["initializeHostedFields"],
        on: {
          submit: "savePaymentSource",
          cancel: "finished",
        },
      },
      savePaymentSource: {
        // @see https://github.com/statelyai/xstate/issues/1010
        // @ts-ignore - This is a known problem on this version of XState. An update to XState will fix it.
        invoke: {
          src: "savePaymentSource",
          onDone: "finished",
          onError: [
            {
              target: "waitForInput",
              cond: "hasValidationErrors",
            },
            {
              target: "errorSaving",
              actions: [
                assign({
                  error: (_context, event) => event.data,
                }),
              ],
            },
          ],
        },
      },
      errorSaving: {
        entry: ["logToSentry", "destroyHostedFields"],
        on: {
          retry: "setup",
          cancel: "finished",
        },
      },
      finished: {
        type: "final",
      },
    },
  },
  {
    actions: {
      clearErrors: assign(context => ({ ...context, error: undefined })),
      logToSentry: (context: Context, event: Event) => {
        if (!isErrorPlatformEvent(event)) return;
        if (event.data?.stripeError?.type === "card_error") return;

        Sentry.captureException(event.data, scope => {
          scope.setTag("package", "payment");
          scope.setTransactionName(event.data.transactionName);
          if (event.data.graphQLErrors) {
            scope.setExtra(
              "GQL errors",
              JSON.stringify(event.data.graphQLErrors)
            );
            delete event.data.graphQLErrors;
          }
          if (event.data.networkError) {
            scope.setExtra("Network error", event.data.networkError);
            delete event.data.networkError;
          }
          scope.setExtra("error", event.data);
          if (context.setupIntent?.setupIntentId) {
            scope.setContext("Setup Intent", {
              setupIntentId: context.setupIntent.setupIntentId,
            });
          }
          return scope;
        });
      },
    },
    guards: {
      hasValidationErrors: (_context, event) =>
        event.type === "error.platform.savePaymentSource" &&
        event.data instanceof ValidationError,
    },
  }
);

export const createSetupIntentMutation = gql`
  mutation createSetupIntentMutation(
    $accountId: Int!
    $processor: PaymentSourceBackend
  ) {
    createSetupIntent(accountId: $accountId, processor: $processor) {
      setupIntentId
      clientSecret
      processor
    }
  }
`;

export const useSetupIntentMachine = (
  processor: PaymentProcessor,
  options: Partial<MachineOptions<Context, Event>> = { services: {} }
) => {
  const { useMutation } = useApolloInjection();
  const accountId = useCurrentAccountId();
  const [createSetupIntent] = useMutation(createSetupIntentMutation, {
    variables: { accountId, processor },
  }) as MutationTuple<
    createSetupIntentMutationType,
    createSetupIntentMutationVariables
  >;

  const [state, send, interpreter] = useMachine<Context, Event>(
    setupIntentMachine,
    {
      ...options,
      services: {
        ...options.services,
        createSetupIntent() {
          const makeSetupIntentCreationError = (errors: GraphQLError[]) =>
            Promise.reject(
              new SetupIntentCreationError(
                "Failed creating setup intent due to GraphQL errors",
                errors
              )
            );

          return timeoutAfter(
            10000,
            new SetupIntentCreationError(
              "Timed out trying to create setup intent after 10s"
            ),
            createSetupIntent().then(
              ({ data, errors }) => {
                //@ts-ignore
                if (errors) return makeSetupIntentCreationError(errors);
                return data?.createSetupIntent;
              },
              error => {
                return makeSetupIntentCreationError(error.graphQLErrors);
              }
            )
          );
        },
      },
    }
  );

  /**
   * Wait for dependencies of this machine to be loaded before transitioning to the "ready" state
   */
  useEffect(() => {
    if (accountId) {
      send("ready");
    }
  }, [accountId, send]);

  return [state, send, interpreter] as const;
};
